import { useState, useEffect, useRef } from 'react';
import { Map as MapIcon, MapPin, Globe2, CalendarDays, Plane, Footprints, ListChecks, ArrowRight, Trash2, ChevronDown, DollarSign, Handshake } from 'lucide-react';
import { getExpandedDays, getHistoricAvg, getRegionsLabel } from '../utils/tripUtils';
import { computeItineraryGaps } from '../utils/itineraryGaps';
import { getTripTiming } from '../utils/travelState';
import { useLocalDate } from '../hooks/useLocalDate';
import { WeatherDisplay } from './WeatherDisplay';
import { Card, Chip, SLabel, EmptyState, CardTintStreaks, InlineEmpty } from './UI';
import { AvatarGroup } from './UserAvatar';
import { RollingCountdown } from './TripIdentitySummary';
import { ConfirmDeleteModal } from './Itinerary';
import { User, TripConfig, Day, Expense, ChecklistItem, Transport } from '../types';
import { computeMyBalance, expenseBaseCurrency, isLiveExpense } from '../utils/expenseUtils';
import { expenseSummaryMessage, type BranchExpenseSummary } from '../utils/branchExpenseSummary';
import { TalismanMark } from './Icons';
import { L1_BY_KEY, normalizeSubcat } from '../constants/taxonomy';
import { SubcategoryIcon } from './SubcategoryIcon';

/** What an item with no subcategory of its own reads as on the Home cards.
 *  Canonical L2 names, so the emoji beside them comes from the same lookup
 *  as every other card in the app. */
const PLAN_DEFAULT_SUBCAT: Record<string, string> = {
  experiences: 'Landmark', food: 'Restaurant', nightlife: 'Other',
};
import { collection, doc, setDoc, deleteDoc, query, orderBy, where, documentId } from 'firebase/firestore';
import { liveQuery, reconnectOnForeground } from '../utils/liveQuery';
import { db } from '../services/firebase';
import { generateDestinationDetails } from '../services/itineraryParserService';
import { destTitle, destinationForDate, transportDuringVisit } from '../utils/destinationItems';
import type { PersonalHomeRoute } from '../utils/itineraryBranches';
import { visitForDate } from '../../functions/src/travellerRoutePolicy.mjs';
import { clockTimeOf } from '../utils/tripUtils';
import { formatClock } from '../utils/schedulePrefs';
import { homeTripSummary } from '../utils/homeTripSummary';
import { MemoryComposer } from './MemoryComposer';
import { homeSchedulePlans, type HomeSchedule, type HomePlan } from '../utils/homeSchedulePlans';

// GET_STARTED shows this once a trip has a title (getTripTiming's 'setup'
// status also requires valid dates and a destination) — names the ONE thing
// actually missing instead of a generic "finish setting up," matching H02's
// "next actions reflect real missing work." Checked in entry order (dates
// before destinations), matching the trip-creation wizard's own step order.
function describeSetupGap(config: TripConfig): { headline: string; body: string } {
  const hasValidDates = !!(config.startDate && config.endDate && config.startDate <= config.endDate);
  if (!hasValidDates) {
    return { headline: 'Add your travel dates', body: "Set when you're leaving and returning to start building your itinerary." };
  }
  if (!config.destinations?.length) {
    return { headline: 'Add a destination', body: "Choose where you're headed to start planning your days." };
  }
  // Shouldn't normally be reached if this agrees with getTripTiming's own
  // 'setup' criteria, but a safe, honest fallback if it ever doesn't.
  return { headline: 'Finish setting up your trip', body: 'Check your travel dates and add destinations to start planning.' };
}

// Match the countdown's idea of a session: once per trip in this page load,
// rather than sessionStorage (which can survive an iOS PWA reopening).
const postTripHighlightsShownThisPageLoad = new Set<string>();

function PostTripHighlights({ tripId, summary, region, travellers }: { tripId: string; summary: ReturnType<typeof homeTripSummary>; region: string; travellers: User[] }) {
  const [animate] = useState(() => !postTripHighlightsShownThisPageLoad.has(tripId));
  useEffect(() => {
    const timer = setTimeout(() => postTripHighlightsShownThisPageLoad.add(tripId), 3400);
    return () => clearTimeout(timer);
  }, [tripId]);

  const highlights = [
    [summary.days, summary.days === 1 ? 'day' : 'days', CalendarDays],
    [summary.destinations, summary.destinations === 1 ? 'destination' : 'destinations', MapPin],
    [summary.countries, summary.countries === 1 ? 'country' : 'countries', Globe2],
    [summary.flights, summary.flights === 1 ? 'flight' : 'flights', Plane],
  ] as const;

  return (
    <div className="flex flex-col items-center gap-6 text-center" aria-label="Trip highlights">
      {region && <div className={`font-serif italic text-2xl text-app-text/90 text-balance ${animate ? 'home-highlight-reveal' : ''}`}>{region}</div>}
      {/* A centred block of left-aligned rows: centring each row on its own
          made the icons zig-zag with the length of each label. */}
      <div className="flex flex-col items-start gap-4 w-fit mx-auto">
      {highlights.map(([count, label, Icon], index) => (
        <div key={label} className={`flex items-center justify-start gap-3 ${animate ? 'home-highlight-reveal' : ''}`}
          style={animate ? { animationDelay: `${(index + 1) * 500}ms` } : undefined}>
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gold/10" aria-hidden="true">
            <Icon className="size-4 text-gold" />
          </span>
          <span className="flex items-baseline gap-1.5 whitespace-nowrap">
            <span className="font-serif text-2xl font-semibold leading-none text-app-text" style={{ fontVariantNumeric: 'lining-nums tabular-nums' }}>{count}</span>
            <span className="font-serif text-lg leading-none text-app-sub">{label}</span>
          </span>
        </div>
      ))}
      </div>
      <div className={`flex flex-wrap items-center justify-center gap-2 border-t border-app-border/60 pt-5 w-full ${animate ? 'home-highlight-reveal' : ''}`}
        style={animate ? { animationDelay: '2500ms' } : undefined}>
        <AvatarGroup users={travellers} size={24} max={6} twoInitials />
        <span className="text-sm font-normal text-app-sub/70">{travellers.length} {travellers.length === 1 ? 'traveller' : 'travellers'}</span>
      </div>
    </div>
  );
}

// The "You're owed" / "You owe" / "You're all square!" expense-balance card —
// shared by TravellingInsight (during the trip) and HomeTab's POST_TRIP
// section (after it), so outstanding costs use one visual pattern and one
// navigation target in both places.
function ExpenseBalanceCard({ myBalance, baseCurrency, hasLiveExpenses, onNavigate, summary }: {
  myBalance: number; baseCurrency: string; hasLiveExpenses: boolean;
  summary?: BranchExpenseSummary;
  onNavigate?: (tab: 'home' | 'logistics' | 'bucketlist' | 'dayplanner' | 'expenses' | 'memories') => void;
}) {
  if (summary && summary.status !== 'ready') return <Card className="p-4"><p role="status" className="text-sm text-app-sub">{expenseSummaryMessage[summary.status]}</p></Card>;
  if (summary) { myBalance = summary.balance!; baseCurrency = summary.currency; hasLiveExpenses = summary.count > 0; }
  return (
    <button
      onClick={() => { (window as any).__pendingExpenseTab = 'settle'; onNavigate?.('expenses'); }}
      className="w-full text-left"
    >
      <Card className={myBalance > 0.01 || myBalance < -0.01 ? "border-app-border/40 overflow-hidden hover:shadow-md transition-shadow" : "p-2 border-app-border/40 overflow-hidden"}>
        {myBalance > 0.01 ? (
          <div className="flex items-center gap-3 p-3">
            <div className="w-10 h-10 rounded-2xl bg-app-faint flex items-center justify-center shrink-0">
              <DollarSign className="w-5 h-5 text-gold" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="ds-label">You're owed</div>
              <div className="text-sm font-sans text-emerald-700 mt-0.5">
                {baseCurrency} {myBalance.toFixed(2)}
              </div>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-gold opacity-20 group-hover:opacity-80 group-hover:translate-x-0.5 transition shrink-0" />
          </div>
        ) : myBalance < -0.01 ? (
          <div className="flex items-center gap-3 p-3">
            <div className="w-10 h-10 rounded-2xl bg-app-faint flex items-center justify-center shrink-0">
              <DollarSign className="w-5 h-5 text-gold" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="ds-label">You owe</div>
              <div className="text-sm font-sans text-rose-600 mt-0.5">
                {baseCurrency} {Math.abs(myBalance).toFixed(2)}
              </div>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-gold opacity-20 group-hover:opacity-80 group-hover:translate-x-0.5 transition shrink-0" />
          </div>
        ) : (
          <InlineEmpty
            className="hover:opacity-60 transition-opacity"
            icon={<Handshake className="w-4 h-4 text-app-sub" />}
            title="You're all square!"
            body={hasLiveExpenses
              ? "All expenses have been settled"
              : <>Log spending and settlements in <span className="font-black">Expenses</span> to get started</>}
          />
        )}
      </Card>
    </button>
  );
}

function TravellingInsight({ currentDay, tripConfig, routeTrip, route, openSchedule, openPrivate, itineraryDays, onNavigate, user, expenseSummary }: { currentDay: Day; tripConfig: TripConfig; routeTrip: TripConfig; route?: PersonalHomeRoute; openSchedule: (date: string) => void; openPrivate: (date: string) => boolean; itineraryDays: Day[]; onNavigate?: (tab: 'home' | 'logistics' | 'bucketlist' | 'dayplanner' | 'expenses' | 'memories') => void; user: User; expenseSummary?: BranchExpenseSummary }) {
  // ── Helpers ──────────────────────────────────────────────────────────────
  const fmtDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr.length === 10 ? dateStr + 'T12:00' : dateStr);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };
  // The clock time in a stored value, or '' — a date-only value has none.
  // This used to take the first five characters of anything, so a leg saved
  // with a date but no time read "Departing 27 Sep at 2026-".
  const fmtTime = clockTimeOf;

  // 1. Upcoming — merged transport departures + accommodation check-ins, chronological
  const todayStr = currentDay.date; // YYYY-MM-DD
  const twoDaysLaterDate = new Date(todayStr + 'T00:00:00Z');
  twoDaysLaterDate.setUTCDate(twoDaysLaterDate.getUTCDate() + 1);
  const twoDaysLaterStr = twoDaysLaterDate.toISOString().slice(0, 10);

  const upcomingTransports = itineraryDays
    .flatMap((d, dayIdx) =>
      (d.transports || []).filter(t => !t.isRental).map(t => ({
        kind: 'transport' as const,
        t,
        date: d.date,
        dayIdx,
        sortKey: t.dep?.includes('T') ? t.dep : `${d.date}T${fmtTime(t.dep || '') || '00:00'}`,
      }))
    )
    .filter(item => item.date >= todayStr && item.date <= twoDaysLaterStr);

  const upcomingAccoms = (routeTrip.accommodations || [])
    .map(a => {
      const ciDate = a.checkIn?.includes('T') ? a.checkIn.split('T')[0] : (a.checkIn || '');
      const dayIdx = itineraryDays.findIndex(d => d.date === ciDate);
      const resolvedDayIdx = dayIdx >= 0 ? dayIdx : itineraryDays.findIndex(d => d.date >= ciDate);
      return {
        kind: 'accommodation' as const,
        a,
        date: ciDate,
        dayIdx: resolvedDayIdx,
        city: itineraryDays[resolvedDayIdx]?.city || a.area || '',
        sortKey: a.checkIn?.includes('T') ? a.checkIn : `${ciDate}T00:00`,
      };
    })
    .filter(item => item.date >= todayStr && item.date <= twoDaysLaterStr && item.date !== '');

  const upcomingItems = [...upcomingTransports, ...upcomingAccoms]
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

  // Show Next Moves first only when it contains an entry for today
  const nextMovesHasToday = upcomingItems.some(item => item.date === todayStr);

  // 2. Today's saved schedule, then the remaining destination Shortlist.
  // A personal route reads the visit's own scope (main trip or private branch)
  // and its source schedule doc, never a clipped date.
  const routeOwner = route?.visits.find(v => v.visit === visitForDate(route.visits.map(r => r.visit), todayStr));
  const scheduleScope = route ? routeOwner?.source.id ?? tripConfig.id : tripConfig.id;
  const scheduleOwner = route ? routeOwner?.scheduleId : destinationForDate(tripConfig.destinations || [], todayStr)?.arrivalDate;
  const scheduleKey = `${scheduleScope}/${scheduleOwner}/${todayStr}`;
  const [savedSchedule, setSavedSchedule] = useState<{ key: string; value: HomeSchedule | null } | null>(null);
  useEffect(() => {
    if (!scheduleOwner) return;
    const sub = liveQuery('home-schedule',
      () => query(collection(db, 'trips', scheduleScope, 'schedules'), where(documentId(), '==', scheduleOwner)),
      snapshot => setSavedSchedule({ key: scheduleKey, value: snapshot.docs[0]?.data().schedule || null }));
    const stopForeground = reconnectOnForeground([sub]);
    return () => { stopForeground(); sub.stop(); };
  }, [scheduleScope, scheduleOwner, scheduleKey]);
  const scheduleReady = !scheduleOwner || savedSchedule?.key === scheduleKey;
  const currentDayIdx = itineraryDays.findIndex(d => d.date === currentDay.date);
  const matchesDest = (d: Day) =>
    currentDay.destinationId ? d.destinationId === currentDay.destinationId : d.city === currentDay.city;
  let stayStart = currentDayIdx;
  while (stayStart > 0 && matchesDest(itineraryDays[stayStart - 1])) stayStart--;
  let stayEnd = currentDayIdx;
  while (stayEnd < itineraryDays.length - 1 && matchesDest(itineraryDays[stayEnd + 1])) stayEnd++;
  const destDays = itineraryDays.slice(stayStart, stayEnd + 1);

  const rawPlans = destDays.flatMap(d => [
    ...(d.attractions || []).map(a => ({ ...a, type: 'Attraction', l1Key: 'experiences', date: d.date })),
    ...(d.eats || []).map(e => ({ ...e, type: 'Dining', l1Key: 'food', date: d.date })),
    ...(d.nightlife || []).map(n => ({ ...n, type: 'Nightlife', l1Key: 'nightlife', date: d.date })),
    ...(d.shopping || []).map(n => ({ ...n, type: 'Shopping', l1Key: 'shopping', date: d.date })),
    ...(d.notes || []).map(n => ({ ...n, type: 'Note', l1Key: 'other', date: d.date })),
  ]);
  const uniquePlansMap = new Map<string, HomePlan>();
  rawPlans.forEach(p => { if (p.id && !uniquePlansMap.has(p.id)) uniquePlansMap.set(p.id, p); });
  const allPlans = Array.from(uniquePlansMap.values());

  const { scheduled, shortlisted } = homeSchedulePlans(
    savedSchedule?.key === scheduleKey ? savedSchedule.value : null, todayStr, allPlans,
    Object.values(routeTrip.itinerary || {}).flatMap(day => day.transports || []));

  // 3. Outstanding Expenses — current user's net REMAINING balance (after
  // confirmed settlements), computed by the same formula as the Expenses settle
  // tab so both screens always agree.  Uses isLiveExpense internally, so
  // soft-deleted expenses are excluded.
  const expenses = (tripConfig.expenses || []) as Expense[];
  const travellers = (tripConfig.travellers || []).filter(
    u => !u.inviteStatus || u.inviteStatus === 'accepted' || u.inviteStatus === 'added',
  );
  const myBalance = computeMyBalance(
    expenses,
    user.id,
    travellers,
    tripConfig.destinations || [],
  );
  // expenseBaseCurrency, the rule Expenses uses, not the raw field: a trip with
  // no explicit base currency falls back to the owner's home country, and the
  // balance here is computed in that currency. Reading the field alone showed
  // the same number with no currency on it.
  const baseCurrency = expenseBaseCurrency(tripConfig);

  const navigateAndDispatch = (tab: 'home' | 'logistics' | 'bucketlist' | 'dayplanner' | 'expenses' | 'memories', eventName: string, detail: any) => {
    if (!onNavigate) return;
    onNavigate(tab);
    [100, 400, 700, 1200, 2000].forEach(delay => {
      setTimeout(() => window.dispatchEvent(new CustomEvent(eventName, { detail })), delay);
    });
  };

  const handlePlanClick = (p: any) => {
    if (!onNavigate) return;
    // The Shortlist follows the personal route and opens the item in its own scope.
    navigateAndDispatch('bucketlist', 'nav-itinerary-day', `itinerary-${p.id}`);
  };

  const planRow = (p: HomePlan, scheduled: boolean) => {
    const l1Key = p.l1Key;
    const subcat = normalizeSubcat(l1Key === 'food' ? p.meal || p.category : p.category)
      || PLAN_DEFAULT_SUBCAT[l1Key] || 'Other';
    return <button key={p.id} type="button"
      onClick={() => scheduled ? openSchedule(todayStr) : handlePlanClick(p)}
      className="w-full text-left p-3 flex items-center gap-3 hover:bg-app-faint/50 transition-colors group">
      <div className="w-9 h-9 rounded-xl bg-app-faint flex items-center justify-center shrink-0 group-hover:bg-white transition-colors">
        <SubcategoryIcon name={subcat} l1={l1Key} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-app-text truncate">{p.name}</div>
        <div className="ds-label mt-0.5">{subcat}</div>
      </div>
      {scheduled && p.start && <span className="text-xs text-app-sub tabular-nums shrink-0">{formatClock(p.start)}</span>}
      <ArrowRight className="w-3.5 h-3.5 text-gold shrink-0" />
    </button>;
  };

  return (
    <div className="flex flex-col gap-6">
      <div className={`flex flex-col gap-1.5 pt-2 ${nextMovesHasToday ? 'order-1' : 'order-2'}`}>
        <SLabel>Next Moves</SLabel>
        <Card className="p-2 border-app-border/40 overflow-hidden">
          {upcomingItems.length > 0 ? (
            <div className="divide-y divide-app-border/10">
              {upcomingItems.map((item, i) => {
                if (item.kind === 'transport') {
                  const t = item.t;
                  const depRaw = t.dep || '';
                  const depDate = depRaw.includes('T') ? depRaw.split('T')[0] : item.date;
                  const depTime = fmtTime(depRaw);
                  const arrRaw = t.arr || '';
                  const arrDate = arrRaw.includes('T') ? arrRaw.split('T')[0] : '';
                  const arrTime = fmtTime(arrRaw);

                  // Calculate how many days after departure the arrival falls
                  let dayOffset = 0;
                  if (depRaw.includes('T') && arrRaw.includes('T')) {
                    const depMs = new Date(depRaw.slice(0, 10) + 'T12:00').getTime();
                    const arrMs = new Date(arrRaw.slice(0, 10) + 'T12:00').getTime();
                    dayOffset = Math.round((arrMs - depMs) / 86400000);
                  } else if (depTime && arrTime) {
                    // Time-only: if arrival is earlier in the day than departure, assume +1
                    const [dH, dM] = depTime.split(':').map(Number);
                    const [aH, aM] = arrTime.split(':').map(Number);
                    if (aH * 60 + aM < dH * 60 + dM) dayOffset = 1;
                  }

                  // For flights, prefer IATA codes; fall back to city name or full name
                  const extractCode = (full: string) => full?.match(/\(([A-Z]{3})\)/)?.[1] ?? '';
                  const fromLabel = t.mode === 'Flight'
                    ? (t.fromCode || extractCode(t.from) || t.fromCity || t.from)
                    : (t.fromCity || t.from);
                  const toLabel = t.mode === 'Flight'
                    ? (t.toCode || extractCode(t.to) || t.toCity || t.to)
                    : (t.toCity || t.to);

                  return (
                    <div
                      key={`t-${i}`}
                      onClick={() => item.dayIdx >= 0 && !openPrivate(item.date) && navigateAndDispatch('bucketlist', 'nav-itinerary-day', `itinerary-${item.t.id || item.t.flightNum || String(item.t.dep)}`)}
                      className="p-3 flex items-center gap-3 hover:bg-app-faint/50 transition-colors cursor-pointer group"
                    >
                      <div className="w-9 h-9 bg-app-faint rounded-xl flex items-center justify-center shrink-0 group-hover:bg-white transition-colors">
                        <SubcategoryIcon name={t.mode} l1="transport" className="w-5 h-5 text-gold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-app-text truncate">
                          {fromLabel} → {toLabel}
                        </div>
                        <div className="ds-label mt-0.5">
                          {t.mode}{t.carrier ? ` · ${t.carrier}` : ''}
                        </div>
                        <div className="mt-1.5 space-y-0.5">
                          <div className="text-[13px] text-app-sub">
                            Departing {fmtDate(depDate)}{depTime ? ` at ${formatClock(depTime)}` : ''}
                          </div>
                          {arrTime && (
                            <div className="text-[13px] text-app-sub/70">
                              Arriving at {formatClock(arrTime)}{dayOffset > 0 ? ` (+${dayOffset})` : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-gold opacity-20 group-hover:opacity-80 group-hover:translate-x-0.5 transition shrink-0" />
                    </div>
                  );
                } else {
                  const a = item.a;
                  const ciRaw = a.checkIn || '';
                  const ciDate = ciRaw.includes('T') ? ciRaw.split('T')[0] : ciRaw;
                  const ciTime = ciRaw.includes('T') ? fmtTime(ciRaw.split('T')[1]) : '';
                  const coRaw = a.checkOut || '';
                  const coDate = coRaw.includes('T') ? coRaw.split('T')[0] : coRaw;
                  return (
                    <div
                      key={`a-${i}`}
                      onClick={() => item.dayIdx >= 0 && !openPrivate(item.date) && navigateAndDispatch('bucketlist', 'nav-itinerary-day', `itinerary-${item.a.id}`)}
                      className="p-3 flex items-center gap-3 hover:bg-app-faint/50 transition-colors cursor-pointer group"
                    >
                      <div className="w-9 h-9 bg-app-faint rounded-xl flex items-center justify-center shrink-0 group-hover:bg-white transition-colors">
                        <SubcategoryIcon name={a.category} l1="accommodation" className="w-5 h-5 text-gold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-app-text truncate">{a.name}</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {item.city && (
                            <>
                              <span className="ds-label">
                                {item.city}
                              </span>
                              <span className="text-app-border/40 text-xs">·</span>
                            </>
                          )}
                          <span className="ds-label">
                            {a.category || 'Hotel/Resort'}
                          </span>
                        </div>
                        <div className="mt-1.5 space-y-0.5">
                          <div className="text-[13px] text-app-sub">
                            Checking in {fmtDate(ciDate)}{ciTime ? ` at ${formatClock(ciTime)}` : ''}
                          </div>
                          {coDate && (
                            <div className="text-[13px] text-app-sub/70">
                              Checking out {fmtDate(coDate)}
                            </div>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-gold opacity-20 group-hover:opacity-80 group-hover:translate-x-0.5 transition shrink-0" />
                    </div>
                  );
                }
              })}
            </div>
          ) : (
            <InlineEmpty
              icon={<Plane className="w-4 h-4 text-app-sub" />}
              title="Kick back and relax!"
              body="No transits or check‑ins for the next two days"
            />
          )}
        </Card>
      </div>

      <div className={`flex flex-col gap-1.5 pt-2 ${nextMovesHasToday ? 'order-2' : 'order-1'}`}>
        <SLabel>Today’s schedule</SLabel>
        <Card className="p-2 border-app-border/40 overflow-hidden">
          {!scheduleReady ? <p role="status" className="p-3 text-sm text-app-sub">Loading today’s schedule…</p> : scheduled.length > 0 ? (
            <div className="divide-y divide-app-border/10 flex flex-col">
              {scheduled.map(p => planRow(p, true))}
            </div>
          ) : (
            <InlineEmpty
              onClick={() => openSchedule(todayStr)}
              icon={<Footprints className="w-4 h-4 text-app-sub" />}
              title="Nothing scheduled for today"
              body="Open Schedule to plan your day."
            />
          )}
        </Card>
        {scheduleReady && shortlisted.length > 0 && <details key={scheduleKey} className="group mt-3">
          <summary className="flex items-center justify-between cursor-pointer list-none py-2 [&::-webkit-details-marker]:hidden">
            <SLabel>Shortlisted</SLabel>
            <ChevronDown className="w-4 h-4 text-app-sub transition-transform group-open:rotate-180" />
          </summary>
          <Card className="p-2 border-app-border/40 overflow-hidden">
            <div className="divide-y divide-app-border/10">{shortlisted.map(p => planRow(p, false))}</div>
          </Card>
        </details>}
      </div>

      <div className="flex flex-col gap-1.5 pt-2 order-4">
        <SLabel>Expenses</SLabel>
        <ExpenseBalanceCard
          myBalance={myBalance}
          summary={expenseSummary}
          baseCurrency={baseCurrency}
          hasLiveExpenses={expenses.filter(isLiveExpense).length > 0}
          onNavigate={onNavigate}
        />
      </div>
    </div>
  );
}


// ── Checklist row ─────────────────────────────────────────────────────────────
// A grey trash icon sits to the right of every row, on both mobile and
// desktop (previously hidden below the md breakpoint — mobile had to rely
// on the swipe gesture alone). Either it or the swipe-reveal panel opens the
// same confirm-delete portal before anything is actually removed; neither
// used to confirm at all.
const DELETE_WIDTH = 72; // px revealed when swiped open

function SwipeableChecklistItem({
  item,
  onToggle,
  onDelete,
  onRename,
  isFirst,
  isLast,
}: {
  item: { id: string; item: string; checked: boolean };
  onToggle: () => void;
  onDelete: () => void;
  onRename: (text: string) => void;
  isFirst?: boolean;
  isLast?: boolean;
}) {
  const [offset, setOffset]   = useState(0);
  const [open, setOpen]       = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(item.item);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const inputRef    = useRef<HTMLInputElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const isDragging  = useRef(false);
  const rowRef      = useRef<HTMLDivElement>(null);

  // Keep draft in sync if item text changes externally (e.g. Firestore update)
  useEffect(() => { setDraft(item.item); }, [item.item]);

  // Focus input when entering edit mode
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commitEdit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== item.item) onRename(trimmed);
    else setDraft(item.item); // revert if empty or unchanged
  };

  // Close swipe-open state when anything outside this row is tapped
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (rowRef.current && !rowRef.current.contains(e.target as Node)) {
        setOpen(false);
        setOffset(0);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;

    // Ignore if the gesture is more vertical than horizontal
    if (!isDragging.current && Math.abs(dy) > Math.abs(dx)) return;
    isDragging.current = true;

    const base = open ? -DELETE_WIDTH : 0;
    const next = Math.min(0, Math.max(-DELETE_WIDTH, base + dx));
    setOffset(next);
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    const threshold = DELETE_WIDTH * 0.4;
    // If closed: open when swiped left past threshold
    // If open:   close only when swiped right past threshold (back toward 0)
    const nextOpen = open
      ? offset < -(DELETE_WIDTH - threshold)  // still open unless swiped most of the way back
      : offset < -threshold;                  // open if swiped far enough left
    setOpen(nextOpen);
    setOffset(nextOpen ? -DELETE_WIDTH : 0);
    isDragging.current = false;
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const handleDelete = () => {
    setOpen(false);
    setOffset(0);
    setConfirmingDelete(true);
  };

  const isTransitioning = !isDragging.current;

  return (
    <div
      ref={rowRef}
      className={`relative overflow-hidden ${isFirst ? '' : 'border-t border-app-border/10'}`}
    >
      {/* Delete button — sits behind the row, revealed by sliding left */}
      <div
        className="absolute inset-y-0 right-0 flex items-center justify-center bg-rose-500"
        style={{ width: DELETE_WIDTH }}
      >
        <button
          onMouseDown={e => e.stopPropagation()}
          onClick={handleDelete}
          className="w-full h-full flex flex-col items-center justify-center gap-0.5 text-white active:brightness-90 transition"
          aria-label="Delete item"
        >
          <Trash2 className="w-4 h-4" />
          <span className="text-[9px] font-black uppercase tracking-widest">Delete</span>
        </button>
      </div>

      {/* Row content — slides left on swipe */}
      <div
        className="relative bg-app-surface flex items-center gap-3 py-3 px-3"
        style={{
          transform: `translateX(${offset}px)`,
          transition: isTransitioning ? 'transform 0.25s ease' : 'none',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Checkbox — only this toggles completion */}
        <button
          onClick={() => { if (!isDragging.current) { setEditing(false); onToggle(); } }}
          className="tap-target relative shrink-0 py-0.5"
          aria-label={item.checked ? 'Mark incomplete' : 'Mark complete'}
        >
          <div className={`w-5 h-5 rounded-lg border transition-all flex items-center justify-center ${item.checked ? 'bg-gold border-gold text-white' : 'border-gold/30 hover:border-gold/60'}`}>
            {item.checked && <span className="text-xs drop-shadow-sm font-bold">✓</span>}
          </div>
        </button>

        {/* Text — tap to edit inline */}
        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              ref={inputRef}
              maxLength={200}
              name="checklist-edit"
              autoComplete="off"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                if (e.key === 'Escape') { setDraft(item.item); setEditing(false); }
              }}
              className="w-full text-xs font-medium bg-transparent border-b border-gold/40 focus:border-gold outline-none text-app-text py-0.5"
            />
          ) : (
            <button
              onClick={() => { if (!isDragging.current && !open) setEditing(true); }}
              className={`text-xs font-medium text-left w-full transition-all ${item.checked ? 'text-app-sub line-through opacity-40' : 'text-app-text'}`}
            >
              {item.item}
            </button>
          )}
        </div>

        {/* Always-visible grey trash — opens the confirm portal below rather
            than deleting immediately. */}
        <button
          onClick={() => setConfirmingDelete(true)}
          className="tap-target relative shrink-0 flex items-center justify-center p-2 text-app-sub/30 hover:text-rose-500 rounded-lg transition-colors"
          aria-label="Delete item"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {confirmingDelete && (
        <ConfirmDeleteModal
          title="Delete this item?"
          message={`"${item.item}" will be permanently removed from your checklist.`}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => { onDelete(); setConfirmingDelete(false); }}
        />
      )}
    </div>
  );
}

export function HomeTab({ user, tripConfig, route, routeError, onOpenScope, hasSavedTrips = false, onAddTrip, onUpdateConfig, onNavigate, expenseSummary }: {
  user: User;
  /** This traveller's continuous route (personalHomeRoute): undefined = the
   *  root trip is their route, null = private branch content still loading. */
  route?: PersonalHomeRoute | null;
  routeError?: string;
  /** Open a private branch's itinerary; root tabs can't show its days yet. */
  onOpenScope?: (branchId: string) => void;
  expenseSummary?: BranchExpenseSummary;
  tripConfig: TripConfig | null;
  /** True when the user has at least one saved trip in Firestore. */
  hasSavedTrips?: boolean;
  onAddTrip?: () => void;
  onUpdateConfig?: (c: TripConfig) => void;
  onNavigate?: (tab: 'home' | 'logistics' | 'bucketlist' | 'dayplanner' | 'expenses' | 'memories') => void;
  // trips/activeTripId/onSelectTrip/onDeleteTrip used to be threaded through
  // here for the "Your Trip" switcher carousel; that card lives on the
  // Itinerary tab now (see TripOverview), so Home no longer needs them.
}) {
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [newItem, setNewItem] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [visibleCarouselIdx, setVisibleCarouselIdx] = useState(0);
  const [loadedMemoryIds, setLoadedMemoryIds] = useState<string[]>([]);
  const today = useLocalDate();

  useEffect(() => {
    if (!tripConfig?.id || !user?.id) return;

    const checklistRef = collection(db, 'trips', tripConfig.id, 'userChecklists', user.id, 'items');
    const q = query(checklistRef, orderBy('createdAt', 'asc'));

    // liveQuery, not a bare onSnapshot: with no error handler one dropped
    // stream froze the checklist until a reload.
    const sub = liveQuery('checklist', () => q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as ChecklistItem);
      setChecklist(items);
    });
    const stopForeground = reconnectOnForeground([sub]);
    return () => { stopForeground(); sub.stop(); };
  }, [tripConfig?.id, user?.id]);

  // Cycle through the full archive, loading the target/next photos progressively.
  // Retain loaded slides so crossfades never uncover a blank background.
  const postTripMemories = [...(tripConfig?.memories || [])]
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  const targetMemoryId = postTripMemories[carouselIdx % postTripMemories.length]?.id;
  useEffect(() => {
    if (postTripMemories.length <= 1) return;
    const t = setInterval(() => setCarouselIdx(i => (i + 1) % postTripMemories.length), 5000);
    return () => clearInterval(t);
  }, [postTripMemories.length]);
  useEffect(() => {
    if (targetMemoryId && loadedMemoryIds.includes(targetMemoryId)) setVisibleCarouselIdx(carouselIdx);
  }, [carouselIdx, loadedMemoryIds, targetMemoryId]);

  // ── Lazy emoji migration ─────────────────────────────────────────────────
  // If a trip was saved before generateDestinationDetails was live, all
  // destination emojis will be '📍'. Detect this and silently backfill them.
  const emojiMigrationRef = useRef<string | null>(null);

  useEffect(() => {
    if (!tripConfig?.id || !onUpdateConfig) return;
    // Only someone actually on the trip writes to it. Invite-only trips are
    // shown too (so the invitee can see what they've been asked to join), and
    // the rules refuse a pending invitee's edits — a background save from here
    // would surface as a "Couldn't save trip" popup for someone who did nothing.
    const onTrip = tripConfig.ownerId === user.id
      || (tripConfig.memberIds ?? []).includes(user.id)
      || (tripConfig.adminIds ?? []).includes(user.id);
    if (!onTrip) return;

    // Run at most once per trip load (tracked by trip ID)
    if (emojiMigrationRef.current === tripConfig.id) return;

    const dests = tripConfig.destinations ?? [];
    if (dests.length === 0) return;

    const needsMigration = dests.some(d => !d.emoji || d.emoji === '📍' || !d.blurb);
    if (!needsMigration) return;

    emojiMigrationRef.current = tripConfig.id;

    generateDestinationDetails(
      dests.map(d => ({ city: d.name, country: d.country ?? null }))
    )
      .then(details => {
        const updatedDests = dests.map((d, i) => {
          const needsEmoji = !d.emoji || d.emoji === '📍';
          const needsBlurb = !d.blurb;
          if (!needsEmoji && !needsBlurb) return d;
          return {
            ...d,
            ...(needsEmoji && { emoji: details[i]?.emoji || '📍' }),
            ...(needsBlurb && { blurb: details[i]?.blurb || '' }),
          };
        });
        const changed = updatedDests.some((d, i) => d.emoji !== dests[i].emoji || d.blurb !== dests[i].blurb);
        if (changed) onUpdateConfig({ ...tripConfig, destinations: updatedDests });
      })
      .catch(err => {
        console.warn('Lazy emoji migration failed:', err);
        // Allow retry on next mount if the CF wasn't deployed yet
        emojiMigrationRef.current = null;
      });
  // Only re-run when the trip ID changes — not on every destination update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripConfig?.id]);

  const toggleItem = async (itemId: string, currentChecked: boolean) => {
    if (!tripConfig?.id || !user?.id) return;
    try {
      const itemRef = doc(db, 'trips', tripConfig.id, 'userChecklists', user.id, 'items', itemId);
      await setDoc(itemRef, { checked: !currentChecked }, { merge: true });
    } catch (err) {
      console.error("Error toggling checklist item:", err);
    }
  };

  const addItem = async () => {
    if (!newItem.trim() || !tripConfig?.id || !user?.id) return;
    const itemId = `item-${Date.now()}`;
    const itemData: ChecklistItem = {
      id: itemId,
      item: newItem.trim(),
      checked: false,
      createdAt: new Date().toISOString()
    };
    try {
      const itemRef = doc(db, 'trips', tripConfig.id, 'userChecklists', user.id, 'items', itemId);
      await setDoc(itemRef, itemData);
      setNewItem("");
    } catch (err) {
      console.error("Error adding checklist item:", err);
    }
  };

  const deleteItem = async (itemId: string) => {
    if (!tripConfig?.id || !user?.id) return;
    try {
      const itemRef = doc(db, 'trips', tripConfig.id, 'userChecklists', user.id, 'items', itemId);
      await deleteDoc(itemRef);
    } catch (err) {
      console.error("Error deleting checklist item:", err);
    }
  };

  const renameItem = async (itemId: string, text: string) => {
    if (!tripConfig?.id || !user?.id) return;
    try {
      const itemRef = doc(db, 'trips', tripConfig.id, 'userChecklists', user.id, 'items', itemId);
      await setDoc(itemRef, { item: text }, { merge: true });
    } catch (err) {
      console.error("Error renaming checklist item:", err);
    }
  };

  // Reorder: Incomplete at top, completed at bottom
  const sortedChecklist = [...checklist].sort((a, b) => {
    if (a.checked === b.checked) {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }
    return a.checked ? 1 : -1;
  });

  if (!tripConfig) {
    // User has saved trips loading — don't flash GET_STARTED while data arrives
    if (hasSavedTrips) {
      return <div className="space-y-6 animate-fade-up" />;
    }
    return (
      <div className="animate-fade-up flex-1 flex flex-col">
        <EmptyState
          // Home keeps the compass brandmark rather than the nav's house glyph —
          // this is the first screen a new account ever sees.
          // The Talisman brandmark, minus its dashed surround — the token it
          // sits in is already a circle. Sized well above the tabs' lucide
          // glyphs: the mark only draws the middle 60% of its own viewBox, and
          // what it draws there is a hairline crosshair at half opacity, so it
          // reads far lighter than a solid glyph of the same nominal size.
          icon={<TalismanMark size={100} outerRing={false} />}
          label="Begin your journey"
          title="Where to next?"
          body="From bustling cities to white sand beaches, start imagining your next trip with Talisman"
          actionLabel="Create a trip"
          onAction={onAddTrip}
        />
      </div>
    );
  }

  // Safeguard: if the user has saved trips but tripConfig has no title (e.g. an
  // abandoned pendingTrip that hasn't been cleared yet), suppress GET_STARTED.
  if (hasSavedTrips && !tripConfig.title.trim()) {
    return <div className="space-y-6 animate-fade-up" />;
  }

  // Missing private content is a loading state, never a reason to show the
  // main route instead (plan.md, Continuous personal routes).
  if (route === null) {
    return <p role="status" className="py-10 text-center text-app-sub">{routeError || 'Loading your itinerary…'}</p>;
  }
  // Route reads (dates, stops, days, bookings) follow this traveller; money,
  // checklists, memories and every write stay on the root trip.
  const routeTrip = route?.config ?? tripConfig;
  const privateScope = (date: string) => route?.visits.find(v => v.visit === visitForDate(route.visits.map(r => r.visit), date))?.source.branchId;
  const openPrivate = (date: string) => {
    const scope = privateScope(date);
    if (scope) onOpenScope?.(scope);
    return !!scope;
  };
  const timing = getTripTiming(routeTrip, today);

  const hasTrip = tripConfig.title.trim() !== "";
  const setupGap = describeSetupGap(tripConfig);
  // Outstanding-costs card — same canonical formula and card as
  // TravellingInsight's own Expenses section, so every mode agrees.
  // Shared by COUNTDOWN (pending decisions) and POST_TRIP (what's left to finish).
  const myOutstandingExpenses = (tripConfig.expenses || []) as Expense[];
  const myOutstandingTravellers = (tripConfig.travellers || []).filter(
    u => !u.inviteStatus || u.inviteStatus === 'accepted' || u.inviteStatus === 'added',
  );
  const myOutstandingBalance = computeMyBalance(myOutstandingExpenses, user.id, myOutstandingTravellers, tripConfig.destinations || []);
  const isBefore = timing.status === 'countdown';
  const isDuring = timing.status === 'active';
  
  type HomeMode = 'GET_STARTED' | 'COUNTDOWN' | 'TRAVELLING' | 'POST_TRIP';
  
  const homeModes: Record<typeof timing.status, HomeMode> = {
    setup: 'GET_STARTED', countdown: 'COUNTDOWN', active: 'TRAVELLING', 'post-trip': 'POST_TRIP',
  };
  const mode = homeModes[timing.status];
  const daysUntil = timing.daysUntil;
  
  const itineraryDays = getExpandedDays(routeTrip);
  // On a personal route the Schedule resolves the visit from the date;
  // otherwise it indexes the root trip's days (= itineraryDays).
  const openSchedule = (date: string) => {
    if (!onNavigate) return;
    onNavigate('dayplanner');
    const [name, detail] = route ? ['nav-schedule-date', date] : ['nav-itinerary-day', Math.max(0, itineraryDays.findIndex(d => d.date === date))];
    [50, 150, 300, 500].forEach(delay => setTimeout(() => window.dispatchEvent(new CustomEvent(name, { detail })), delay));
  };
  const essentialGaps = computeItineraryGaps(itineraryDays, routeTrip, {
    onDestinationsGap() {}, onScrollToDestination() {}, onScrollToTransport() {},
    onOpenAccommodation() {}, onOpenTransport() {},
  }).filter(insight => insight.group === 'essential');
  // Same region logic the "Your Trip" card's gold pill uses — shared via
  // getRegionsLabel rather than duplicated; this card just renders it as
  // serif italic instead of a chip.
  const regionsLabel = getRegionsLabel(routeTrip, itineraryDays);
  const activeTravellers = tripConfig.travellers.filter(u => u.inviteStatus !== 'pending');
  const tripSummary = homeTripSummary(routeTrip, itineraryDays);
  const navigateAndDispatch = (tab: 'home' | 'logistics' | 'bucketlist' | 'dayplanner' | 'expenses' | 'memories', eventName: string, detail: any) => {
    if (!onNavigate) return;
    onNavigate(tab);
    [50, 150, 300, 500].forEach(delay => {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent(eventName, { detail }));
      }, delay);
    });
  };

  const tripDaysPassed = timing.dayNumber;
  const tripProgress = itineraryDays.length > 0 
    ? Math.min(100, Math.max(0, Math.round((timing.elapsedDays / itineraryDays.length) * 100)))
    : 0;

  const currentDay = itineraryDays.find(d => d.date === today)
    || (isBefore ? (itineraryDays[0] || null) : (itineraryDays[itineraryDays.length - 1] || null));

  const getHistoricAvgLocal = (city: string): string => {
    const month = new Date(`${today}T12:00`).toLocaleDateString("en-GB", { month: "short" });
    return getHistoricAvg(city, month);
  };

  const isGapDay = currentDay?.city === "Gap in Itinerary";

  // Destination blurb for the feature card, TRAVELLING mode only — the same
  // free-text description generated for the destination when it was added
  // (getDestinationInfo, stored on Destination.blurb), matched by city name
  // the same way computeItineraryGaps' destIdxForCity does (dest.name is
  // "City, Country"; currentDay.city is just "City").
  const currentDestBlurb = !isGapDay && currentDay
    ? (routeTrip.destinations || []).find(d => destTitle(d.name) === currentDay.city)?.blurb
    : undefined;

  // Extracted to a variable (rather than left inline) so it can be rendered
  // in two different positions depending on mode — above "First Transit"
  // for COUNTDOWN, after TravellingInsight for TRAVELLING — without
  // duplicating the JSX.
  const checklistSection = (
    <div className="flex flex-col gap-1.5 pt-2">
      <SLabel className="px-1">Checklist</SLabel>
      <Card className="p-2 border-app-border/40 space-y-2">
        {(() => {
          const unchecked = sortedChecklist.filter(i => !i.checked);
          const completed = sortedChecklist.filter(i => i.checked);
          return sortedChecklist.length === 0 ? (
            <InlineEmpty
              icon={<ListChecks className="w-4 h-4 text-app-sub" />}
              title="Never forget a thing"
              body="Add tasks and items to tick off before you go"
            />
          ) : (
            <div>
              {unchecked.map((itemObj, idx) => (
                <SwipeableChecklistItem
                  key={itemObj.id}
                  item={itemObj}
                  onToggle={() => toggleItem(itemObj.id, itemObj.checked)}
                  onDelete={() => deleteItem(itemObj.id)}
                  onRename={(text) => renameItem(itemObj.id, text)}
                  isFirst={idx === 0}
                  isLast={idx === unchecked.length - 1 && completed.length === 0}
                />
              ))}
              {completed.length > 0 && (
                <div className={unchecked.length > 0 ? 'mt-1 pt-1 border-t border-app-border/20' : ''}>
                  <button
                    onClick={() => setShowCompleted(v => !v)}
                    className="w-full flex items-center gap-2 py-2.5 px-3 text-left hover:opacity-70 transition-opacity"
                  >
                    <ChevronDown className={`w-3.5 h-3.5 text-app-sub/50 transition-transform shrink-0 ${showCompleted ? 'rotate-180' : ''}`} />
                    <span className="ds-label">
                      {completed.length} completed task{completed.length !== 1 ? 's' : ''}
                    </span>
                  </button>
                  {showCompleted && (
                    <div>
                      {completed.map((itemObj, idx) => (
                        <SwipeableChecklistItem
                          key={itemObj.id}
                          item={itemObj}
                          onToggle={() => toggleItem(itemObj.id, itemObj.checked)}
                          onDelete={() => deleteItem(itemObj.id)}
                          onRename={(text) => renameItem(itemObj.id, text)}
                          isFirst={idx === 0}
                          isLast={idx === completed.length - 1}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
        {/* Less space above (space-y-2 on the Card already provides some),
            more below — was the reverse (pt-2 stacked on top of space-y-2,
            nothing extra below beyond the Card's own p-2). */}
        <div className="px-3 pb-2">
          <input
            name="checklist-new-item"
            autoComplete="off"
            // The rules refuse items over 200 characters; without the cap a
            // longer one just silently never saved.
            maxLength={200}
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
            onBlur={addItem}
            placeholder="Add item..."
            className="w-full bg-app-faint/50 border border-app-border/40 rounded-xl px-4 py-3.5 text-xs font-medium focus:border-gold focus:outline-none placeholder:opacity-30"
          />
        </div>
      </Card>
    </div>
  );

  const memoryCarousel = postTripMemories.length > 0 && (
    <div className="relative w-full overflow-hidden rounded-2xl bg-app-faint">
      <button type="button" aria-label="Open memory archive" onClick={() => onNavigate?.('memories')}
        className={`block w-full text-left focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-gold ${mode === 'POST_TRIP' ? 'relative aspect-[3/4]' : 'relative h-56 sm:h-72'}`}>
        {postTripMemories.map((memory, index) => (
          (loadedMemoryIds.includes(memory.id) || index === visibleCarouselIdx % postTripMemories.length || index === carouselIdx % postTripMemories.length || index === (carouselIdx + 1) % postTripMemories.length) &&
          <img key={memory.id} src={memory.imageUrl} alt={memory.caption || 'Trip memory'}
            referrerPolicy="no-referrer"
            onLoad={() => setLoadedMemoryIds(ids => ids.includes(memory.id) ? ids : [...ids, memory.id])}
            className={`absolute inset-0 w-full h-full object-cover motion-safe:transition-opacity motion-safe:duration-700 ${index === visibleCarouselIdx % postTripMemories.length ? 'opacity-100' : 'opacity-0'}`} />
        ))}
        {postTripMemories[visibleCarouselIdx % postTripMemories.length].caption && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-4 pt-10 pb-4 text-white pointer-events-none">
            <p className="font-serif italic text-base line-clamp-2">“{postTripMemories[visibleCarouselIdx % postTripMemories.length].caption}”</p>
          </div>
        )}
        <span className="absolute right-3 top-3 rounded-xl bg-white/60 px-3 py-2 text-sm font-semibold text-app-text">View all memories →</span>
      </button>
      {postTripMemories.length > 1 && (
        <div className="flex items-center justify-center bg-app-surface py-1">
          {postTripMemories.length > 7 ? <>
            <button type="button" aria-label="Previous memory" onClick={() => setCarouselIdx(i => (i - 1 + postTripMemories.length) % postTripMemories.length)} className="px-4 py-2 text-app-sub">←</button>
            <span className="text-xs text-app-sub tabular-nums">{visibleCarouselIdx % postTripMemories.length + 1} / {postTripMemories.length}</span>
            <button type="button" aria-label="Next memory" onClick={() => setCarouselIdx(i => (i + 1) % postTripMemories.length)} className="px-4 py-2 text-app-sub">→</button>
          </> : postTripMemories.map((memory, index) => (
            <button key={memory.id} type="button" aria-label={`Show memory ${index + 1}`}
              aria-current={index === visibleCarouselIdx % postTripMemories.length}
              onClick={() => setCarouselIdx(index)} className="p-2.5">
              <span className={`block h-1.5 rounded-full transition-all ${index === visibleCarouselIdx % postTripMemories.length ? 'w-4 bg-gold' : 'w-1.5 bg-app-border'}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const memoriesSection = (
    <section className="flex flex-col gap-3 pt-2" aria-label="Memories">
      {mode === 'TRAVELLING' && <SLabel>Memories</SLabel>}
      {memoryCarousel}
      <MemoryComposer tripConfig={tripConfig} user={user} variant={postTripMemories.length ? 'compact' : 'empty'}>Add memory</MemoryComposer>
    </section>
  );

  return (
    <div className="space-y-6 animate-fade-up">

      {/* Main Feature Card */}
      {mode === 'GET_STARTED' ? (
        <Card className="overflow-hidden border border-app-border shadow-none relative">
          <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: `url("https://www.transparenttextures.com/patterns/natural-paper.png")` }} />
          <div className="pt-8 pb-5 px-10 relative text-center text-balance overflow-hidden">
            <div className="animate-fade-up">
              <div className="w-20 h-20 bg-gold/5 rounded-full flex items-center justify-center mx-auto mb-8 transition-colors">
                <MapIcon className="w-10 h-10 text-gold/30" />
              </div>
              <SLabel className="mb-2">{hasTrip ? 'Your next step' : 'Begin your journey'}</SLabel>
              <div className="text-app-text font-serif text-4xl font-bold mb-5 px-4 leading-tight tracking-tight text-center text-balance">
                {hasTrip ? setupGap.headline : 'Where to next?'}
              </div>
              <p className="font-serif italic text-app-sub/70 text-lg max-w-[280px] mx-auto leading-relaxed mb-10 text-center text-balance">
                {hasTrip ? setupGap.body : 'Plan your trip, travel together, and keep the moments worth remembering.'}
              </p>

              <button
                onClick={hasTrip ? () => onNavigate?.('logistics') : onAddTrip}
                className="w-full p-6 bg-gold text-white rounded-3xl group transition shadow-xl shadow-gold/20 hover:brightness-110 active:scale-[0.98] flex flex-col items-center gap-2"
              >
                <div className="text-[11px] font-black uppercase tracking-widest">{hasTrip ? 'Continue planning' : 'Create Trip'}</div>
              </button>
            </div>
          </div>
        </Card>
      ) : (
        <>
          {/* ── Main feature card ────────────────────────────────────────────
              The "Your Trip" switcher carousel that briefly lived above this
              has moved back to the Itinerary tab, so this is once again the
              single hero card on Home, covering every mode. COUNTDOWN's
              rolling-digit countdown is inset here rather than in its own
              section below (it had been pulled out into one while the
              switcher card held the top slot). */}
          <Card className="overflow-hidden border border-app-border shadow-none relative" style={mode === 'POST_TRIP' ? { contain: 'paint' } : undefined}>
            <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: `url("https://www.transparenttextures.com/patterns/natural-paper.png")` }} />
            {mode === 'POST_TRIP' && <CardTintStreaks />}
            {/* Tint-clip wrapper: covers the headline content AND the merged
                region/blurb/travellers block below it — both are inside
                this same wrapper now (moved in for TRAVELLING, harmless for
                every other mode: overflow-hidden with nothing absolutely
                positioned to clip does nothing). The Trip Progression bar
                stays a sibling outside this wrapper entirely, so tint can
                never reach past the line above it. */}
            <div className="relative z-10 overflow-hidden" style={{ contain: 'paint' }}>
              {/* contain: paint is the actual fix for streaks still visibly
                  crossing the line below this wrapper — plain overflow:
                  hidden can fail to clip a blurred, animated descendant on
                  Safari/WebKit once it's promoted to its own GPU
                  compositing layer for the animation; contain: paint forces
                  this element to be the guaranteed clip boundary for
                  everything painted inside it, filters and animated
                  transforms included. */}
              {/* Countdown and travelling tint stays clipped above the footer;
                  post-trip tint instead spans the whole feature card. */}
              {(mode === 'COUNTDOWN' || mode === 'TRAVELLING') && <CardTintStreaks />}
            <div className={`pt-8 pb-5 relative z-10 text-center text-balance overflow-hidden ${mode === 'POST_TRIP' ? 'px-5' : 'px-10'}`}>
              {/* Reverted to plain SLabel — the smaller one-off size was
                  a bigger change than intended; the 25-char cap on the Trip
                  Title field (see FormInput's maxLength) is kept, and does
                  the actual job of keeping this on one line. */}
              <SLabel className="mb-2">{tripConfig.title}</SLabel>
              {mode === 'COUNTDOWN' ? (
                /* The rolling digit boxes stand in for what was a plain
                   serif number; the card owns the "Days Until Departure"
                   caption here, so RollingCountdown's own copy is switched
                   off. Spacing tightened from the original restore
                   (mt-10/mb-10 around the caption) — that gap was sized for
                   a much lighter plain-number countdown and read as far too
                   much empty space once the boxed countdown (which already
                   carries its own visual weight) took its place. */
                <div className="animate-fade-up px-4">
                  <div className="flex items-center justify-center mt-2 mb-5">
                    <RollingCountdown days={daysUntil} tripId={tripConfig.id} showCaption={false} />
                  </div>
                  {/* First attempt (text-sm/60 muted grey) read as too
                      understated for what's the card's actual headline
                      claim. Bigger, warmer, and closer to full opacity —
                      still non-serif proper case, not the all-caps tracked
                      label style used elsewhere on the card. */}
                  <div className="font-sans text-lg font-bold text-app-text/80 mb-5">
                    {daysUntil === 1 ? 'Day' : 'Days'} until departure
                  </div>
                  {/* The departure-date pill is gone — it repeated the date
                      already visible under the trip title, further up on
                      Home, without being actionable. A "View itinerary" link
                      does something instead. */}
                  {onNavigate && (
                    <button
                      onClick={() => onNavigate('logistics')}
                      className="hover:brightness-110 active:scale-95 transition"
                    >
                      <Chip label="View itinerary →" color="#c5a059" size="sm" solid />
                    </button>
                  )}
                </div>
              ) : mode === 'POST_TRIP' ? (
                <div>
                  <div className="text-app-text font-serif font-bold mt-10 mb-8 text-5xl tracking-tight leading-none">
                    Memories
                  </div>
                  <PostTripHighlights key={tripConfig.id} tripId={tripConfig.id} summary={tripSummary} region={regionsLabel} travellers={activeTravellers} />
                </div>
              ) : (
                <div className="animate-fade-up">
                  {/* A gap day gets its own headline ("Exploring the
                      unknown?") in the same serif style as a real city — the
                      big dashed "Add destination" CTA that used to take over
                      this card is gone. Filling gaps belongs to the
                      Itinerary tab's own gaps CTA and modal, which handles
                      it properly per-gap. The subtext below is sans-serif
                      (font-sans), unlike every other line in this card —
                      deliberately, to read as a plain explanatory caption
                      rather than more editorial copy. */}
                  <div className={`font-serif font-bold mt-10 tracking-tight text-balance ${isGapDay ? 'text-4xl text-app-sub/40 mb-3' : 'text-5xl text-app-text mb-6'}`}>
                    {isGapDay ? "Exploring the unknown?" : (currentDay?.city || "En Route")}
                  </div>
                  {isGapDay ? (
                    <p className="text-app-sub/60 text-sm font-sans leading-relaxed mb-8 max-w-[240px] mx-auto text-balance">
                      You haven't added a destination to your itinerary for today.
                    </p>
                  ) : (
                    <div className="flex items-center justify-center gap-4 text-app-text text-lg font-serif mb-5">
                      {currentDay && (
                        <>
                          <span>{currentDay.flag} {currentDay.country}</span>
                          <span className="w-1.5 h-1.5 rounded-full bg-app-border/40" />
                          <WeatherDisplay
                            city={currentDay.city}
                            date={currentDay.date}
                            initialWeather={getHistoricAvgLocal(currentDay.city)}
                          />
                        </>
                      )}
                    </div>
                  )}

                  <div className="flex justify-center gap-2">
                    {currentDay && onNavigate && (
                      <button
                        onClick={() => {
                          if (isGapDay) {
                            // Deep-link straight to the Itinerary tab's
                            // focused single-destination "Add" panel for
                            // today's specific gap, instead of just landing
                            // on the Shortlist day view — see
                            // handleNavOverview in ItineraryTab.
                            onNavigate('logistics');
                            setTimeout(() => window.dispatchEvent(new CustomEvent('nav-itinerary-overview', { detail: currentDay.date })), 80);
                            return;
                          }
                          // Schedule, not Shortlist — "Today's Schedule"
                          // means the day's plan, which is what Schedule
                          // shows. handleNavDay (ItineraryTab) now also syncs
                          // Schedule's own activeDate when it sets
                          // activeDayIdx, so this lands on today specifically
                          // rather than the destination's first day.
                          openSchedule(currentDay.date);
                        }}
                        className="hover:brightness-110 active:scale-95 transition"
                      >
                        <Chip label={isGapDay ? "+ Update itinerary" : "Today's Schedule →"} color="#C5A059" size="sm" solid />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Region + travellers block for pre-trip and travelling: a hairline
                divider, the region in editorial serif
                italic (not the gold chip TripIdentitySummary uses), then the
                avatar row. Region is hidden while TRAVELLING, where the
                current-city headline above already says where you are —
                the destination's own blurb takes that slot instead, below
                the Today's Schedule pill and above the traveller count, in
                its own white containerised box. TRAVELLING also drops the
                hairline divider itself (kept for every other mode) — with
                the tint now extending into this block too (see the wrapper
                above), a visible line here read as an unnecessary seam. */}
            {mode !== 'POST_TRIP' && <div className={`w-[90%] mx-auto flex flex-col items-center justify-center gap-3 pb-5 ${mode === 'TRAVELLING' ? 'pt-4' : 'pt-4 border-t-[0.5px] border-app-border'}`}>
              {regionsLabel && mode !== 'TRAVELLING' && (
                <div className="text-app-text/90 font-serif italic text-2xl leading-relaxed max-w-[280px] text-balance">
                  {regionsLabel}
                </div>
              )}
              {mode === 'TRAVELLING' && currentDestBlurb && (
                <div className="relative z-10 w-full px-3 pt-2 pb-5 mb-2">
                  <p className="text-app-sub/70 text-sm font-sans leading-relaxed text-center text-balance">
                    {currentDestBlurb}
                  </p>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <AvatarGroup users={activeTravellers} size={24} max={6} twoInitials />
                <span className="text-[13px] font-normal text-app-sub/70 whitespace-nowrap">
                  {activeTravellers.length} {activeTravellers.length === 1 ? 'traveller' : 'travellers'}
                </span>
              </div>
            </div>}
            </div>

            {isDuring && (
              <div className="p-4 px-6 bg-app-surface border-t border-app-border space-y-3">
                <div className="flex justify-between items-center text-[11px] font-black tracking-widest uppercase">
                  <span className="text-app-sub font-bold">Trip Progression</span>
                  <span className="text-app-text">Day {tripDaysPassed} of {itineraryDays.length}</span>
                </div>
                <div className="bg-app-faint h-1.5 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition duration-1000 bg-gold"
                    style={{ width: `${tripProgress}%` }}
                  />
                </div>
              </div>
            )}
          </Card>
          {mode === 'POST_TRIP' && memoriesSection}
        </>
      )}

      {/* Dynamic Sections */}
      <div className="space-y-6">
        {mode === 'COUNTDOWN' ? (
          <>
            {/* Before departure, fill itinerary gaps first, then settle costs. */}
            {essentialGaps.length > 0 && (
              <div className="flex flex-col gap-1.5 pt-2">
                <SLabel className="px-1">Itinerary gaps</SLabel>
                <Card
                  className="px-4 py-2 border-app-border/40 transition hover:shadow-md cursor-pointer group"
                  onClick={() => navigateAndDispatch('logistics', 'nav-itinerary-gaps', undefined)}
                >
                  <div className="flex flex-col divide-y divide-app-border/20">
                    {essentialGaps.map(insight => {
                      const Icon = insight.type === 'DESTINATION' ? MapPin : L1_BY_KEY[insight.type.toLowerCase()].icon;
                      return (
                        <div key={insight.id} className="flex items-center gap-4 py-2.5">
                          <div className="w-10 h-10 bg-app-faint rounded-2xl flex items-center justify-center shrink-0 opacity-70">
                            <Icon className="w-5 h-5 text-gold" strokeWidth={1.7} aria-hidden="true" />
                          </div>
                          <div className="flex-1 text-app-text text-sm leading-snug">
                            {insight.message.split(/(destination|accommodation|transport)/i).map((part, index) =>
                              /^(destination|accommodation|transport)$/i.test(part)
                                ? <strong key={index} className="font-bold">{part}</strong> : part)}
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-gold opacity-20 group-hover:opacity-80 group-hover:translate-x-0.5 transition shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>
            )}

            {(expenseSummary ? expenseSummary.status !== 'ready' || expenseSummary.count > 0 : myOutstandingExpenses.filter(isLiveExpense).length > 0) && (
              <div className="flex flex-col gap-1.5 pt-2">
                <SLabel className="px-1">Expenses</SLabel>
                <ExpenseBalanceCard
                  myBalance={myOutstandingBalance}
                  summary={expenseSummary}
                  baseCurrency={expenseBaseCurrency(tripConfig)}
                  hasLiveExpenses
                  onNavigate={onNavigate}
                />
              </div>
            )}

            {checklistSection}

            {(() => {
              const allTransports = itineraryDays.flatMap(d => (d.transports || []).map(t => ({ ...t, _dayDate: d.date })));
              const isJourney = (t: Transport) => !t.isRental && !routeTrip.destinations.some(dest => transportDuringVisit(t, dest, allTransports));
              const firstDayWithTransport = itineraryDays.find(d => d.transports?.some(isJourney));
              const firstTransport = firstDayWithTransport?.transports?.find(isJourney);
              const handleFirstTransitClick = firstTransport
                ? () => !openPrivate(firstDayWithTransport!.date) && navigateAndDispatch('bucketlist', 'nav-itinerary-day', `itinerary-${firstTransport.id || firstTransport.flightNum || String(firstTransport.dep)}`)
                : undefined;
              return (
                <div className="flex flex-col gap-1.5 pt-2">
                  <SLabel className="px-1">First Transit</SLabel>
                  <Card
                    className={`px-4 py-2 border-app-border/40 transition-all${firstTransport ? ' hover:shadow-md cursor-pointer group' : ''}`}
                    onClick={handleFirstTransitClick}
                  >
                    {firstTransport ? (
                      <div className="flex items-center justify-between py-2">
                        <div className="flex flex-1 min-w-0 items-center gap-4">
                          <div className="w-10 h-10 bg-app-faint rounded-2xl flex items-center justify-center shrink-0"><SubcategoryIcon name={firstTransport.mode} l1="transport" className="w-5 h-5 text-gold" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="text-app-text font-bold text-sm leading-snug">{firstTransport.mode}{firstTransport.carrier ? ` · ${firstTransport.carrier}` : ''}</div>
                            <div className="text-app-sub text-sm mt-0.5 font-normal tracking-normal break-words">{firstTransport.from || tripConfig.homeLocation || 'Origin'} → {firstTransport.toCity || firstTransport.to || itineraryDays[0].city}</div>
                            <div className="text-app-sub/50 text-sm font-normal tracking-normal mt-1">
                              {(() => {
                                const dep = firstTransport.dep || '';
                                const time = formatClock(dep) || '--:--';
                                const dateStr = firstDayWithTransport!.dateShort;
                                return `Dep ${dateStr} at ${time}`;
                              })()}
                            </div>
                          </div>
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 text-gold opacity-20 group-hover:opacity-80 group-hover:translate-x-0.5 transition shrink-0" />
                      </div>
                    ) : (
                      <InlineEmpty
                        icon={<Plane className="w-4 h-4 text-app-sub" />}
                        title="No transport added yet"
                        body="Add transport routes to your itinerary to see your first transit"
                      />
                    )}
                  </Card>
                </div>
              );
            })()}
          </>
        ) : mode === 'POST_TRIP' ? (
          <>
          {/* Outstanding costs lead post-trip — settling up is the one thing
              still genuinely unfinished once travel itself has ended. */}
          <div className="flex flex-col gap-1.5 pt-2">
            <SLabel className="px-1">Expenses</SLabel>
            <ExpenseBalanceCard
              myBalance={myOutstandingBalance}
              summary={expenseSummary}
              baseCurrency={expenseBaseCurrency(tripConfig)}
              hasLiveExpenses={myOutstandingExpenses.filter(isLiveExpense).length > 0}
              onNavigate={onNavigate}
            />
          </div>
          </>
        ) : mode === 'TRAVELLING' && currentDay ? (
          <TravellingInsight
            expenseSummary={expenseSummary}
            currentDay={currentDay}
            tripConfig={tripConfig}
            routeTrip={routeTrip}
            route={route}
            openSchedule={openSchedule}
            openPrivate={openPrivate}
            itineraryDays={itineraryDays}
            onNavigate={onNavigate}
            user={user}
          />
        ) : mode !== 'GET_STARTED' ? (
          <div className="animate-fade-up flex flex-col gap-1.5 pt-2">
            <SLabel className="px-1">Your Trip</SLabel>
            <Card className="p-8 border-app-border/40 text-center text-balance space-y-3">
              <div className="w-12 h-12 bg-app-faint rounded-2xl flex items-center justify-center mx-auto text-xl grayscale opacity-30">✨</div>
              <div className="text-app-text font-bold text-sm">Waiting for the first step</div>
              <p className="text-app-sub text-sm max-w-[240px] mx-auto">Add destinations and transport to see your trip come to life.</p>
            </Card>
          </div>
        ) : null}

        {/* COUNTDOWN gets the checklist inline above (see checklistSection,
            positioned before First Transit); TRAVELLING still wants it here,
            after TravellingInsight. */}
        {mode === 'TRAVELLING' && memoriesSection}
        {mode === 'TRAVELLING' && checklistSection}
      </div>

    </div>
  );
}
